import { useQuery } from "@tanstack/react-query";
import type { AccessProfile } from "@/lib/nav-visibility";
import { orpc } from "@/utils/orpc";
import { useSession } from "./auth-hooks";

export function useAdminAuth(): {
	isAdmin: boolean;
	isLoading: boolean;
	error: Error | null;
} {
	const { data: session, isLoading: sessionLoading } = useSession();

	const {
		data: result,
		isLoading: permissionLoading,
		error,
	} = useQuery<{ hasAccess: boolean }>({
		...orpc.admin.checkAccess.queryOptions(),
		enabled: !!session?.user,
		retry: false,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});

	return {
		isAdmin: result?.hasAccess ?? false,
		isLoading: sessionLoading || permissionLoading,
		error: error as Error | null,
	};
}

/**
 * Per-permission profile for sidebar gating and the root redirect.
 * `profile` is null while loading (callers should render nothing/skeleton
 * rather than flash the wrong nav).
 */
export function useAccessProfile(): {
	profile: AccessProfile | null;
	isLoading: boolean;
} {
	const { data: session } = useSession();

	const { data, isLoading } = useQuery<AccessProfile>({
		...orpc.admin.getAccessProfile.queryOptions(),
		enabled: !!session?.user,
		retry: false,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});

	return { profile: data ?? null, isLoading };
}
